// Operator dispatch HOLDS (epic / plan / repo scoped) — hand-written client types, API wrappers and
// pure display helpers. Contract: plans/active/ao_epic_hold_respawn_slo_alert_fixes_2026_10_05.md § A.
// Deliberately NOT in src/generated/** (the backend regenerates that); reconcile once it lands.

import { httpRaw } from "./api";

export type HoldScopeKind = "epic" | "plan" | "repo";
export type HoldInFlight = "drain" | "notify" | "stop";

export interface DispatchHold {
  id: number;
  scope_kind: HoldScopeKind;
  scope_value: string;
  reason: string;
  created_by: string;
  created_at: string;
  expires_at: string | null;
  in_flight: HoldInFlight;
  lifted_at: string | null;
  lifted_by: string | null;
  covered_queued: number;
  covered_dispatched: number;
}

export interface DispatchHoldsResponse {
  active: DispatchHold[];
  recently_lifted: DispatchHold[];
  fail_open_count: number;
}

/** Server wire shape of one hold (server/routes/dispatch_holds.py `_view`): counts nest under `covered`. */
export type DispatchHoldWire = Omit<DispatchHold, "covered_queued" | "covered_dispatched"> & {
  covered?: { queued?: number; dispatched?: number };
};

/** Flatten the server's nested `covered` into the client's covered_queued/covered_dispatched. */
export function holdFromWire(w: DispatchHoldWire): DispatchHold {
  const { covered, ...rest } = w;
  return { ...rest, covered_queued: covered?.queued ?? 0, covered_dispatched: covered?.dispatched ?? 0 };
}

interface DispatchHoldsWire {
  active: DispatchHoldWire[];
  recently_lifted: DispatchHoldWire[];
  fail_open_count: number;
}

export interface CreateHoldRequest {
  scope_kind: HoldScopeKind;
  scope_value: string;
  reason: string;
  until_minutes?: number;
  in_flight?: HoldInFlight;
}

export interface EpicRow {
  epic: string;
  queued: number;
  dispatched: number;
  held: number;
  done_24h: number;
  hold_id: number | null;
}

export interface EpicsResponse {
  epics: EpicRow[];
}

/** A confirmed create/release, applied to the views at once; the refetch after it confirms (the epics/holds
 *  endpoints take seconds on a 15k-task backlog, so waiting for them made the UI look unresponsive). */
export type HoldsChange = { kind: "created"; hold: DispatchHold } | { kind: "released"; id: number };

export function applyHoldsChange(data: DispatchHoldsResponse, ch: HoldsChange): DispatchHoldsResponse {
  if (ch.kind === "created") {
    return { ...data, active: [...data.active.filter((h) => h.id !== ch.hold.id), ch.hold] };
  }
  const gone = data.active.find((h) => h.id === ch.id);
  return {
    ...data,
    active: data.active.filter((h) => h.id !== ch.id),
    recently_lifted: gone ? [gone, ...data.recently_lifted] : data.recently_lifted,
  };
}

export function applyEpicChange(rows: EpicRow[], ch: HoldsChange): EpicRow[] {
  return rows.map((r) => {
    if (ch.kind === "created") {
      return ch.hold.scope_kind === "epic" && ch.hold.scope_value === r.epic
        ? { ...r, hold_id: ch.hold.id, held: r.queued }
        : r;
    }
    return r.hold_id === ch.id ? { ...r, hold_id: null, held: 0 } : r;
  });
}

export const dispatchHoldsApi = {
  list: async (baseUrl: string): Promise<DispatchHoldsResponse> => {
    const w = (await httpRaw<DispatchHoldsWire>(baseUrl, "/api/dispatch-holds")).data;
    return { ...w, active: w.active.map(holdFromWire), recently_lifted: w.recently_lifted.map(holdFromWire) };
  },
  create: async (baseUrl: string, body: CreateHoldRequest): Promise<DispatchHold> => {
    // POST answers { hold, covered, in_flight_result } (server/routes/dispatch_holds.py), not a bare hold.
    const w = (
      await httpRaw<{ hold: DispatchHoldWire; covered?: DispatchHoldWire["covered"] }>(baseUrl, "/api/dispatch-holds", {
        method: "POST",
        body: JSON.stringify(body),
      })
    ).data;
    return holdFromWire({ ...w.hold, covered: w.covered ?? w.hold.covered });
  },
  release: async (baseUrl: string, id: number): Promise<void> => {
    await httpRaw<void>(baseUrl, `/api/dispatch-holds/${id}`, { method: "DELETE" });
  },
  epics: async (baseUrl: string): Promise<EpicsResponse> => (await httpRaw<EpicsResponse>(baseUrl, "/api/epics")).data,
};

// ── pure helpers (vitest-covered) ─────────────────────────────────────────

function spanLabel(totalSecs: number): string {
  const secs = Math.max(0, Math.floor(totalSecs));
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

/** Compact age: "45s" / "12m" / "3h" / "2d". */
export function holdAge(iso: string, nowMs: number): string {
  const t = new Date(iso).getTime();
  return isNaN(t) ? "?" : spanLabel((nowMs - t) / 1000);
}

/** "no TTL" / "expires in 42m" / "expired". */
export function holdTtlLabel(expiresAt: string | null, nowMs: number): string {
  if (!expiresAt) return "no TTL";
  const t = new Date(expiresAt).getTime();
  if (isNaN(t)) return "no TTL";
  const secs = (t - nowMs) / 1000;
  return secs <= 0 ? "expired" : `expires in ${spanLabel(secs)}`;
}

/** One banner line per hold: "Dispatch HELD — epic ci_master — <reason> — by <who>, <age> · N queued held". */
export function holdBannerLine(h: DispatchHold, nowMs: number): string {
  return (
    `Dispatch HELD — ${h.scope_kind} ${h.scope_value} — ${h.reason} — by ${h.created_by}, ` +
    `${holdAge(h.created_at, nowMs)} · ${h.covered_queued} queued held`
  );
}

export const BANNER_EXPAND_THRESHOLD = 2;

export interface BannerModel {
  /** "none" renders nothing; "lines" one row per hold; "collapsed" a single "N holds active" row. */
  mode: "none" | "lines" | "collapsed";
  summary: string | null;
  holds: DispatchHold[];
}

/** Collapses to "N holds active" past BANNER_EXPAND_THRESHOLD unless the operator expanded it. */
export function bannerModel(active: DispatchHold[], expanded: boolean): BannerModel {
  if (active.length === 0) return { mode: "none", summary: null, holds: [] };
  if (active.length > BANNER_EXPAND_THRESHOLD && !expanded) {
    return { mode: "collapsed", summary: `${active.length} holds active`, holds: active };
  }
  return { mode: "lines", summary: null, holds: active };
}

export interface HoldFormInput {
  reason: string;
  untilMinutes: string; // raw text field; "" = no TTL
  scopeValue?: string; // only validated for plan/repo creation forms
}

export interface HoldFormResult {
  ok: boolean;
  errors: { reason?: string; untilMinutes?: string; scopeValue?: string };
  untilMinutes?: number;
}

/** Hold modal / panel-form validation: reason required, TTL a positive integer when present. */
export function validateHoldForm(input: HoldFormInput): HoldFormResult {
  const errors: HoldFormResult["errors"] = {};
  if (!input.reason.trim()) errors.reason = "A reason is required";
  if (input.scopeValue !== undefined && !input.scopeValue.trim()) errors.scopeValue = "A scope value is required";
  let untilMinutes: number | undefined;
  const raw = input.untilMinutes.trim();
  if (raw !== "") {
    const n = Number(raw);
    if (!Number.isInteger(n) || n <= 0) errors.untilMinutes = "TTL must be a positive whole number of minutes";
    else untilMinutes = n;
  }
  return { ok: Object.keys(errors).length === 0, errors, untilMinutes };
}

export const IN_FLIGHT_EXPLANATIONS: Record<HoldInFlight, string> = {
  drain: "Running workers finish their current task; nothing new is claimed.",
  notify: "Running workers get an inbox message to commit, anchor and /done (operator_hold).",
  stop: "Running workers are released immediately via pause-and-kill.",
};

/** Epic-row toggle state: held iff a hold id is attached. */
export function epicToggleState(row: Pick<EpicRow, "hold_id">): "held" | "open" {
  return row.hold_id !== null ? "held" : "open";
}

/** Parses the backend's `held: <kind>=<value> by …` blocked_reason into a row badge, else null. */
export function heldBadgeFromBlockedReason(reason: string | null | undefined): string | null {
  if (!reason) return null;
  const m = /^held:\s*(epic|plan|repo)=(\S+)/.exec(reason);
  return m ? `HELD · ${m[1]} ${m[2]}` : null;
}

/** Tasks a release will free: the hold's queued coverage. */
export function releaseFreeCount(h: Pick<DispatchHold, "covered_queued">): number {
  return h.covered_queued;
}

export function reconcileHint(h: Pick<DispatchHold, "scope_kind" | "scope_value">): string | null {
  return h.scope_kind === "epic" ? `/plan-reconcile ${h.scope_value}` : null;
}
